import { useApolloClient } from '@apollo/client';
import { resolveTrackedQueries } from '@cosuno/apollo';
import { useCurrentUser } from '@cosuno/authentication-frontend';
import { useEffectOnce } from '@cosuno/react-hooks';
import axios from 'axios';
import React, { createContext, type PropsWithChildren, useCallback, useState } from 'react';

import Console from '~/packages/console';
import { useInterval } from '~/shared/hooks/useInterval';
import { usePageVisibility } from '~/shared/hooks/usePageVisibility';
import { env } from '~/shared/utils/env';

export const CHECK_PERIOD_IN_SECONDS = 5;

export const RefreshFeatureFlagsContext = createContext<{ refresh: () => Promise<void> }>({
  refresh: () => Promise.resolve(),
});

export const RefreshFeatureFlagsContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [version, setVersion] = useState<string | null>();
  const user = useCurrentUser();
  const apolloClient = useApolloClient();
  const { isPageVisible } = usePageVisibility();

  const pollingReasonable = user?.type !== undefined && isPageVisible;

  const updateVersion = useCallback(async () => {
    try {
      const { data, status } = await axios.get<string | null>(
        `${env.REACT_APP_API_BASE}/internal/feature-flags-version`,
        {
          withCredentials: true,
        },
      );

      if (status === 200) {
        const latestVersion = data !== null ? data.toString() : null;
        if (version !== undefined && version !== latestVersion) {
          // Wait for all `trackedQuery` calls to resolve. Resetting the store
          // while a query is in flight results in an error.
          await resolveTrackedQueries();

          // remove data from apollo cache to re-request it based on the latest values of feature flags
          await apolloClient.resetStore();
        }
        setVersion(latestVersion);
      }
    } catch (error) {
      Console.error(`Error while retrieving feature flags version: ${String(error)}`);
    }
  }, [apolloClient, version]);

  useEffectOnce(
    () => {
      void updateVersion();
    },
    { when: pollingReasonable },
  );

  useInterval(updateVersion, CHECK_PERIOD_IN_SECONDS * 1000, pollingReasonable);

  return (
    <RefreshFeatureFlagsContext.Provider value={{ refresh: updateVersion }}>
      {children}
    </RefreshFeatureFlagsContext.Provider>
  );
};
